/**
 * 五行 (金木水火土) 系统
 * - 25 个怪物体型按五行分类：每行 5 只
 * - 部首 → 元素映射（主流方法）+ 笔画数 fallback
 * - 主导五行 / 缺失五行 判定
 */

export type Element = "金" | "木" | "水" | "火" | "土";
export const ELEMENTS: Element[] = ["金", "木", "水", "火", "土"];

export const ELEMENT_LABELS: Record<Element, string> = {
  金: "金",
  木: "木",
  水: "水",
  火: "火",
  土: "土",
};

export const ELEMENT_TRAITS: Record<Element, string> = {
  金: "刚毅果决 · 秩序守护",
  木: "正直向上 · 生长担当",
  水: "机敏深邃 · 智慧流动",
  火: "炽烈爆发 · 热情感染",
  土: "厚重沉稳 · 大地承载",
};

export const ELEMENT_COLORS: Record<Element, { main: string; sub: string; accent: string; text: string }> = {
  金: { main: "#8a8a9a", sub: "#c8c8d8", accent: "#fffacd", text: "#ffe680" },
  木: { main: "#3a5a3a", sub: "#5a8a5a", accent: "#88dd66", text: "#a8e080" },
  水: { main: "#1a3a5a", sub: "#3a5a8a", accent: "#5a8acc", text: "#88c8ff" },
  火: { main: "#5a2a1a", sub: "#aa3a1a", accent: "#ff6644", text: "#ffaa44" },
  土: { main: "#4a3a2a", sub: "#7a5a3a", accent: "#caa66a", text: "#e0c080" },
};

/**
 * 部首 → 五行 映射（主流方法，综合多源）
 * - 金：金、钅、刂、刀、匕、斤、玉、王、钊、镒、钩、铃、铲、锤、锁、镜、铜、铁、银、铝、锡、铅
 * - 木：木、艹、禾、林、枝、杆、板、树、桂、棠、桑、竹、杉、槐、榆、楠、桐、榕、柏、松
 * - 水：水、氵、冫、雨、云、泉、永、汀、汐、潮、流、浪、涛、瀑、湘、淮、河、海、洋、湖
 * - 火：火、灬、炎、焱、炅、光、日、炬、烛、烧、焚、焰、煌、熙、烤、烘、焙、烟、煤、炮
 * - 土：土、士、圭、圳、垣、堤、坤、城、垂、培、增、墨、坤、塔、墙、墓、场、坛、墼、垦
 */
const ELEMENT_RADICALS: Record<Element, string[]> = {
  金: [
    "金", "钅", "刂", "刀", "匕", "斤", "玉", "王",
    "钊", "镒", "钩", "铃", "铲", "锤", "锁", "镜",
    "铜", "铁", "银", "铝", "锡", "铅", "钶", "钙",
    "钻", "铭", "铜", "锭", "锤", "镖", "镭",
  ],
  木: [
    "木", "艹", "禾", "林", "枝", "杆", "板", "树",
    "桂", "棠", "桑", "竹", "杉", "槐", "榆", "楠",
    "桐", "榕", "柏", "松", "桦", "枫", "柳", "杨",
    "桃", "杏", "梨", "橙", "柚", "桔", "枸", "楷",
    "樱", "桂", "柠", "桂", "枞", "柞", "榆",
  ],
  水: [
    "水", "氵", "冫", "雨", "云", "泉", "永", "汀",
    "汐", "潮", "流", "浪", "涛", "瀑", "湘", "淮",
    "河", "海", "洋", "湖", "泊", "泪", "汗", "洗",
    "池", "沾", "泄", "沼", "沾", "涂", "润", "湛",
    "溪", "漱", "漾", "潇", "潮", "潞", "瀛", "瀚",
  ],
  火: [
    "火", "灬", "炎", "焱", "炅", "光", "日", "炬",
    "烛", "烧", "焚", "焰", "煌", "熙", "烤", "烘",
    "焙", "烟", "煤", "炮", "炸", "烁", "烈", "烹",
    "煦", "焉", "焕", "煌", "熔", "燃", "烁", "焰",
    "灶", "灸", "灼", "熹", "爝", "烁", "烂",
  ],
  土: [
    "土", "士", "圭", "圳", "垣", "堤", "坤", "城",
    "垂", "培", "增", "墨", "塔", "墙", "墓", "场",
    "坛", "墼", "垦", "埋", "填", "坡", "埂", "埠",
    "堰", "堪", "塑", "塔", "境", "墒", "墙", "墨",
    "堆", "堡", "堪", "堤", "堰", "塬", "墚",
  ],
};

/**
 * 笔画数 fallback（传统：1-2 木，3-4 火，5-6 土，7-8 金，9-0 水）
 */
function elementByStroke(strokes: number): Element {
  const last = strokes % 10;
  if (last === 1 || last === 2) return "木";
  if (last === 3 || last === 4) return "火";
  if (last === 5 || last === 6) return "土";
  if (last === 7 || last === 8) return "金";
  return "水"; // 9, 0
}

/**
 * 在汉字中找第一个匹配的五行部首
 * @deprecated 单字符的 includes 检查对大部分汉字无效（视觉上的部首 ≠ JS 字符串子串）
 * 请改用 getElementForChar()
 */
function findElementInChar(char: string): Element | null {
  for (const el of ELEMENTS) {
    for (const rad of ELEMENT_RADICALS[el]) {
      if (char.includes(rad)) return el;
    }
  }
  return null;
}

// =================================================================
// ===   每个常用汉字的部首 → 五行（正确的方式）                 ===
// =================================================================
// 用字面数组构建（去重用 Set），避免 object literal 重复键报错
const CHAR_RADICAL_PAIRS: ReadonlyArray<readonly [string, string]> = [
  // === 金部 ===
  ["王", "玉"], ["玉", "玉"], ["珏", "玉"], ["玫", "玉"], ["玲", "玉"], ["玥", "玉"],
  ["金", "金"], ["钊", "金"], ["钶", "金"], ["钙", "金"], ["钻", "金"],
  ["铭", "金"], ["铜", "金"], ["银", "金"], ["铝", "金"], ["锡", "金"], ["铅", "金"],
  ["铁", "金"], ["铲", "金"], ["锁", "金"], ["镜", "金"], ["钟", "金"],
  ["钢", "金"], ["锋", "金"], ["锐", "金"], ["铸", "金"],
  ["刘", "刂"], ["剑", "刂"], ["剃", "刂"], ["刁", "刂"], ["削", "刂"], ["刻", "刂"],
  ["刀", "刀"], ["刃", "刀"], ["分", "刀"], ["切", "刀"], ["判", "刀"], ["剁", "刀"], ["刺", "刂"], ["剂", "刂"], ["剪", "刀"], ["副", "刂"], ["刮", "刂"], ["列", "刂"], ["刊", "刂"], ["判", "刂"], ["刚", "刂"], ["创", "刂"], ["删", "刂"], ["利", "刂"], ["别", "刂"], ["判", "刂"], ["剔", "刂"], ["剥", "刂"], ["剖", "刂"], ["剥", "刂"], ["剧", "刂"], ["剩", "刂"], ["剪", "刀"], ["副", "刂"], ["割", "刂"], ["剥", "刂"], ["剧", "刂"], ["剐", "刂"], ["剔", "刂"], ["剐", "刂"], ["剥", "刂"], ["剧", "刂"], ["剩", "刂"], ["剪", "刀"], ["割", "刂"], ["剐", "刂"], ["剥", "刂"], ["剧", "刂"], ["剩", "刂"], ["剪", "刀"], ["割", "刂"], ["剞", "刂"], ["剡", "刂"], ["剥", "刂"], ["剧", "刂"], ["剩", "刂"], ["剪", "刀"], ["割", "刂"], ["剞", "刂"], ["剡", "刂"], ["剿", "刂"], ["剡", "刂"], ["剥", "刂"], ["剧", "刂"], ["剩", "刂"], ["剪", "刀"], ["割", "刂"], ["剞", "刂"], ["剡", "刂"], ["剿", "刂"], ["剡", "刂"], ["剥", "刂"], ["剧", "刂"], ["剩", "刂"], ["剪", "刀"], ["割", "刂"],

  // === 木部 ===
  ["木", "木"], ["林", "木"], ["森", "木"], ["本", "木"], ["末", "木"], ["朱", "木"],
  ["朴", "木"], ["机", "木"], ["李", "木"], ["杏", "木"], ["材", "木"],
  ["杰", "木"], ["松", "木"], ["柏", "木"], ["柳", "木"], ["杨", "木"],
  ["桐", "木"], ["桦", "木"], ["枫", "木"], ["桥", "木"], ["槐", "木"], ["榆", "木"],
  ["楠", "木"], ["桂", "木"], ["桃", "木"], ["梨", "木"], ["橙", "木"], ["柚", "木"],
  ["桔", "木"], ["枸", "木"], ["楷", "木"], ["樱", "木"], ["柠", "木"], ["枞", "木"],
  ["柞", "木"], ["杉", "木"], ["果", "木"], ["枝", "木"], ["枯", "木"],
  ["柔", "木"], ["板", "木"], ["楼", "木"], ["棒", "木"],
  ["树", "木"], ["棠", "木"], ["桑", "木"], ["楣", "木"], ["楹", "木"], ["楚", "木"],
  ["检", "木"], ["梓", "木"], ["楞", "木"], ["榕", "木"],
  ["芝", "艹"], ["芭", "艹"], ["芬", "艹"], ["芳", "艹"], ["芹", "艹"], ["芽", "艹"],
  ["花", "艹"], ["芙", "艹"], ["芜", "艹"], ["苇", "艹"], ["芸", "艹"],
  ["苹", "艹"], ["苗", "艹"], ["茂", "艹"], ["茉", "艹"], ["莹", "艹"], ["莺", "艹"],
  ["菊", "艹"], ["菌", "艹"], ["菜", "艹"], ["菱", "艹"], ["菲", "艹"],
  ["萄", "艹"], ["萎", "艹"], ["萱", "艹"], ["蓉", "艹"], ["蕊", "艹"], ["蓬", "艹"],
  ["草", "艹"], ["茶", "艹"], ["荷", "艹"], ["莱", "艹"], ["莲", "艹"], ["萝", "艹"],
  ["秀", "禾"], ["秉", "禾"], ["科", "禾"], ["秒", "禾"], ["种", "禾"], ["秋", "禾"],
  ["香", "禾"], ["秧", "禾"], ["稻", "禾"],
  ["穗", "禾"], ["竹", "竹"], ["笋", "竹"], ["笛", "竹"], ["筐", "竹"], ["筠", "竹"],

  // === 水部 ===
  ["水", "水"], ["氵", "水"], ["永", "水"], ["汀", "水"], ["汐", "水"], ["潮", "水"],
  ["江", "氵"], ["河", "氵"], ["湖", "氵"], ["海", "氵"], ["洋", "氵"], ["涛", "氵"],
  ["浪", "氵"], ["流", "氵"], ["浙", "氵"], ["浦", "氵"], ["浩", "氵"], ["涵", "氵"],
  ["深", "氵"], ["清", "氵"], ["渊", "氵"], ["溪", "氵"], ["源", "氵"],
  ["满", "氵"], ["潇", "氵"], ["潘", "氵"], ["澄", "氵"], ["澜", "氵"], ["沾", "氵"],
  ["沛", "氵"], ["沂", "氵"], ["沅", "氵"], ["沐", "氵"], ["沔", "氵"],
  ["没", "氵"], ["沏", "氵"], ["沙", "氵"],
  ["沈", "氵"], ["沧", "氵"], ["沪", "氵"], ["沫", "氵"], ["沮", "氵"],
  ["沱", "氵"], ["沸", "氵"], ["油", "氵"], ["治", "氵"], ["沼", "氵"],
  ["沿", "氵"], ["泄", "氵"], ["泊", "氵"], ["泌", "氵"], ["泓", "氵"],
  ["泞", "氵"], ["泪", "氵"], ["泱", "氵"], ["泳", "氵"], ["泻", "氵"],
  ["涂", "氵"], ["浚", "氵"], ["浣", "氵"],
  ["浸", "氵"], ["涨", "氵"], ["涩", "氵"],
  ["淙", "氵"], ["渔", "氵"], ["渡", "氵"], ["温", "氵"], ["游", "氵"], ["湾", "氵"],
  ["湿", "氵"], ["溃", "氵"], ["溜", "氵"],
  ["滨", "氵"], ["潜", "氵"], ["潭", "氵"],
  ["激", "氵"], ["濂", "氵"], ["濒", "氵"],
  ["雨", "雨"], ["雪", "雨"], ["霜", "雨"], ["霖", "雨"], ["霏", "雨"], ["霓", "雨"],
  ["霞", "雨"],
  ["云", "云"], ["运", "云"],
  ["泉", "水"], ["冰", "水"], ["永", "水"], ["凝", "冫"], ["凉", "冫"], ["冷", "冫"], ["凌", "冫"], ["冬", "冫"], ["冻", "冫"], ["凹", "冫"], ["冲", "冫"], ["决", "冫"], ["冰", "冫"], ["次", "冫"], ["准", "冫"], ["凉", "冫"], ["凌", "冫"], ["减", "冫"], ["凑", "冫"], ["凛", "冫"], ["凝", "冫"], ["几", "冫"], ["冯", "冫"], ["冰", "冫"], ["次", "冫"], ["准", "冫"], ["凉", "冫"], ["凌", "冫"], ["减", "冫"], ["凑", "冫"], ["凛", "冫"], ["凝", "冫"], ["几", "冫"], ["冯", "冫"], ["凶", "冫"], ["凹", "冫"], ["凸", "凸"], ["凭", "几"], ["凰", "几"], ["凯", "几"], ["凳", "几"], ["凹", "冫"], ["凸", "凸"], ["凭", "几"], ["凰", "几"], ["凯", "几"], ["凳", "几"],

  // === 火部 ===
  ["火", "火"], ["灬", "火"],
  ["炎", "火"], ["焱", "火"], ["炅", "火"], ["烨", "火"], ["煜", "火"], ["燚", "火"],
  ["光", "火"], ["辉", "火"], ["煌", "火"], ["照", "火"],
  ["日", "日"], ["旦", "日"], ["旭", "日"], ["早", "日"], ["时", "日"], ["明", "日"],
  ["星", "日"], ["春", "日"], ["昭", "日"], ["是", "日"], ["昼", "日"], ["显", "日"],
  ["晋", "日"], ["晒", "日"], ["晓", "日"], ["晕", "日"], ["晤", "日"], ["晦", "日"],
  ["晨", "日"], ["景", "日"], ["晴", "日"], ["晶", "日"], ["智", "日"], ["暖", "日"],
  ["暗", "日"], ["暮", "日"], ["暴", "日"], ["曙", "日"],
  ["炬", "火"], ["烛", "火"], ["烧", "火"], ["焚", "火"], ["焰", "火"],
  ["熙", "火"], ["烤", "火"], ["烘", "火"], ["焙", "火"], ["烟", "火"], ["煤", "火"],
  ["炮", "火"], ["炸", "火"], ["烁", "火"], ["烈", "火"], ["烹", "火"], ["煦", "火"],
  ["焉", "火"], ["焕", "火"], ["燃", "火"],
  ["灶", "火"], ["灸", "火"], ["灼", "火"], ["熹", "火"], ["爝", "火"], ["烂", "火"], ["烫", "火"], ["烯", "火"], ["烷", "火"], ["烹", "火"], ["焊", "火"], ["焕", "火"], ["焙", "火"], ["焚", "火"], ["焉", "火"], ["焕", "火"], ["燃", "火"], ["烁", "火"], ["灶", "火"], ["灸", "火"], ["灼", "火"], ["熹", "火"], ["爝", "火"], ["烂", "火"], ["烫", "火"], ["烯", "火"], ["烷", "火"], ["烹", "火"], ["焊", "火"], ["焕", "火"], ["焙", "火"], ["焚", "火"],

  // === 土部 ===
  ["土", "土"], ["士", "土"], ["圭", "土"], ["圳", "土"],
  ["坤", "土"], ["城", "土"], ["堤", "土"], ["垣", "土"],
  ["垂", "土"], ["培", "土"], ["增", "土"], ["墨", "土"], ["塔", "土"], ["墙", "土"],
  ["墓", "土"], ["场", "土"], ["坛", "土"], ["墼", "土"], ["垦", "土"],
  ["埋", "土"], ["填", "土"], ["坡", "土"], ["埂", "土"], ["埠", "土"],
  ["堰", "土"], ["堪", "土"], ["塑", "土"], ["境", "土"], ["墒", "土"],
  ["堆", "土"], ["堡", "土"], ["塬", "土"], ["墚", "土"],
  ["地", "土"], ["坐", "土"], ["均", "土"], ["坎", "土"],
  ["坏", "土"], ["坑", "土"], ["块", "土"], ["坚", "土"], ["坝", "土"],
  ["型", "土"], ["垫", "土"], ["垮", "土"],
  ["垒", "土"], ["垢", "土"], ["垤", "土"], ["垠", "土"],
  ["山", "山"], ["岩", "山"], ["岚", "山"], ["岳", "山"], ["岭", "山"], ["岱", "山"],
  ["峙", "山"], ["峭", "山"], ["峰", "山"], ["峻", "山"], ["崇", "山"], ["崔", "山"],
  ["崖", "山"], ["崭", "山"], ["嵯", "山"], ["嶙", "山"], ["巅", "山"],
  ["石", "石"], ["砚", "石"], ["砝", "石"], ["砸", "石"], ["砰", "石"], ["砾", "石"],
  ["础", "石"], ["硅", "石"], ["硕", "石"], ["硝", "石"], ["硫", "石"], ["硬", "石"],
  ["碗", "石"], ["碘", "石"], ["碟", "石"], ["碧", "石"], ["碰", "石"], ["碱", "石"],
  ["碳", "石"], ["磊", "石"], ["磐", "石"], ["磨", "石"], ["礁", "石"], ["礞", "石"],
  ["示", "示"], ["礼", "示"], ["社", "示"], ["祀", "示"], ["祁", "示"], ["祈", "示"],
  ["祉", "示"], ["祖", "示"], ["祝", "示"], ["神", "示"], ["祟", "示"], ["祠", "示"],
  ["祢", "示"], ["祥", "示"], ["票", "示"], ["祭", "示"], ["祷", "示"], ["祸", "示"],
  ["祺", "示"], ["禀", "示"], ["禁", "示"], ["禄", "示"], ["禅", "示"], ["福", "示"],
  ["禹", "土"], ["禾", "禾"], ["秉", "禾"],
];

/** 数组 → Map（最后一个值生效，自动去重） */
const CHAR_RADICAL: Map<string, string> = new Map(CHAR_RADICAL_PAIRS);

/**
 * 部首 → 五行
 */
const RADICAL_TO_ELEMENT: Record<string, Element> = {
  // 金
  "金": "金", "钅": "金", "刂": "金", "刀": "金", "匕": "金", "斤": "金",
  "玉": "金", "王": "金", "钊": "金", "钩": "金", "铃": "金", "铲": "金",
  "锤": "金", "锁": "金", "镜": "金", "铜": "金", "铁": "金", "银": "金",
  // 木
  "木": "木", "艹": "木", "禾": "木", "竹": "木",
  "林": "木", "枝": "木", "杆": "木", "板": "木", "树": "木", "桂": "木",
  "棠": "木", "桑": "木", "杉": "木", "槐": "木", "榆": "木", "楠": "木",
  "桐": "木", "榕": "木", "柏": "木", "松": "木", "桦": "木", "枫": "木",
  "柳": "木", "杨": "木", "桃": "木", "杏": "木", "梨": "木", "橙": "木",
  // 水
  "水": "水", "氵": "水", "冫": "水", "雨": "水", "云": "水",
  "泉": "水", "永": "水", "汀": "水", "汐": "水", "潮": "水",
  // 火
  "火": "火", "灬": "火", "炎": "火", "焱": "火", "炅": "火",
  "光": "火", "日": "火", "炬": "火", "烛": "火", "烧": "火", "焚": "火",
  "焰": "火", "煌": "火", "熙": "火", "烤": "火", "烘": "火", "焙": "火",
  "烟": "火", "煤": "火", "炮": "火", "炸": "火", "烁": "火", "烈": "火",
  // 土
  "土": "土", "士": "土", "圭": "土", "山": "土", "石": "土",
  "坤": "土", "城": "土", "堤": "土", "垣": "土", "圳": "土",
  "垂": "土", "培": "土", "增": "土", "墨": "土", "塔": "土", "墙": "土",
  "墓": "土", "场": "土", "坛": "土", "垦": "土", "埋": "土", "填": "土",
  "坡": "土", "埠": "土", "堰": "土", "堪": "土", "塑": "土", "境": "土",
};

/**
 * 部首 → 五行（统一映射）
 */
function radicalToElement(rad: string): Element | null {
  return RADICAL_TO_ELEMENT[rad] ?? null;
}

/**
 * 单个汉字 → 五行
 * 1) 优先查 per-char 偏旁表（已知字）
 * 2) 未知字查 ELEMENT_RADICALS（按字面 substring，少数情况下能命中）
 * 3) 都没命中 → 返回 null（调用方走笔画 fallback）
 */
export function getElementForChar(char: string): Element | null {
  // 优先 per-char 偏旁表
  const rad = CHAR_RADICAL.get(char);
  if (rad) {
    const el = radicalToElement(rad);
    if (el) return el;
  }
  // 兜底：按字面 substring 找（只能命中单字本身）
  return findElementInChar(char);
}

/**
 * 判定输入名字的五行属性
 * @returns dominant 主导五行，missing 缺失的五行
 */
export function determineElements(
  raw: string,
  charStrokes?: number[]
): { dominant: Element; counts: Record<Element, number>; missing: Element[] } {
  const counts: Record<Element, number> = { 金: 0, 木: 0, 水: 0, 火: 0, 土: 0 };
  const chars = Array.from(raw).filter((c) => /[\u4e00-\u9fa5]/.test(c));

  // 先按部首匹配
  for (let i = 0; i < chars.length; i++) {
    const found = getElementForChar(chars[i]);
    if (found) {
      counts[found]++;
    } else if (charStrokes && charStrokes[i] !== undefined) {
      // fallback: 笔画数
      counts[elementByStroke(charStrokes[i])]++;
    }
  }

  // 没有任何匹配时（理论上不应该发生），默认按第一个字笔画 fallback
  if (Object.values(counts).every((v) => v === 0)) {
    if (charStrokes && charStrokes[0] !== undefined) {
      counts[elementByStroke(charStrokes[0])]++;
    } else {
      counts.木++; // 兜底
    }
  }

  // 主导 = 计数最高的（多个最高时按 金→木→水→火→土 优先级）
  let dominant: Element = "木";
  let max = -1;
  for (const el of ["金", "木", "水", "火", "土"] as Element[]) {
    if (counts[el] > max) {
      max = counts[el];
      dominant = el;
    }
  }

  // 缺失 = 计数为 0 的（最多显示 3 个）
  const missing = ELEMENTS.filter((el) => counts[el] === 0);

  return { dominant, counts, missing };
}

/**
 * 25 个怪物体型，按五行分组
 * 每行 5 个，按"主流意象"从抽象→具体的顺序排列
 */
export const BODY_TYPES_BY_ELEMENT: Record<Element, string[]> = {
  金: ["锋刃兵", "齿轮傀", "钢骨人", "镜灵", "铜钟"],
  木: ["树人长老", "藤蔓女妖", "菌伞灵", "竹节妖", "木鸢"],
  水: ["水母灯", "冰棱鬼", "潮汐灵", "雨滴精", "深潜者"],
  火: ["烛灵", "炎魔", "灰烬使", "岩浆兽", "硫磺鬼"],
  土: ["山岭巨灵", "泥塑童", "沙偶", "矿石兽", "陶俑"],
};

export const ALL_BODY_TYPES: string[] = [
  ...BODY_TYPES_BY_ELEMENT.金,
  ...BODY_TYPES_BY_ELEMENT.木,
  ...BODY_TYPES_BY_ELEMENT.水,
  ...BODY_TYPES_BY_ELEMENT.火,
  ...BODY_TYPES_BY_ELEMENT.土,
];

/**
 * 给定元素和变体索引 → 返回体型名
 */
export function bodyTypeForElement(element: Element, index: number): string {
  const list = BODY_TYPES_BY_ELEMENT[element];
  return list[index % list.length];
}

/**
 * 给定体型名 → 返回所属元素
 */
export function elementOfBody(bodyType: string): Element {
  for (const el of ELEMENTS) {
    if (BODY_TYPES_BY_ELEMENT[el].includes(bodyType)) return el;
  }
  return "土"; // 兜底
}

// =================================================================
// ===          五行分析（逐字拆解 + 主导 / 缺失原因）            ===
// =================================================================

export interface PerCharMatch {
  char: string;
  element: Element;
  reason: "radical" | "stroke" | "unknown";
  /** 命中的具体部首（如"王"），或笔画 fallback 的末位数 */
  detail: string;
}

export interface ElementAnalysis {
  /** 逐字五行归属 */
  perChar: PerCharMatch[];
  /** 主导五行命中的字（"X 字属 Y"） */
  dominantReasons: string[];
  /** 每个缺失元素的原因（"无 X 部字"） */
  missingReasons: Record<Element, string>;
  /** 整段总结 */
  summary: string;
}

/** 每个元素挑 4-5 个最有代表性的部首用于解释"缺什么" */
const ELEMENT_SAMPLE_RADICALS: Record<Element, string[]> = {
  金: ["金", "钅", "刂", "刀", "匕"],
  木: ["木", "艹", "禾", "林", "桂"],
  水: ["水", "氵", "冫", "雨", "泉"],
  火: ["火", "灬", "炎", "光", "日"],
  土: ["土", "士", "城", "坤", "山"],
};

/**
 * 分析名字的五行构成
 * - 逐字拆：部首命中 → 报部首；部首未命中 → 报笔画末位
 * - 主导原因：列出贡献给主导五行的所有字
 * - 缺失原因：对每个 0 计数的元素，说明"名字中无 ... 等部首"
 */
export function analyzeElements(
  raw: string,
  charStrokes?: number[]
): ElementAnalysis {
  const chars = Array.from(raw).filter((c) => /[\u4e00-\u9fa5]/.test(c));
  const perChar: PerCharMatch[] = [];
  const counts: Record<Element, number> = { 金: 0, 木: 0, 水: 0, 火: 0, 土: 0 };

  for (let i = 0; i < chars.length; i++) {
    const c = chars[i];
    const rad = CHAR_RADICAL.get(c);
    const radHit = getElementForChar(c);
    if (radHit && rad) {
      counts[radHit]++;
      perChar.push({ char: c, element: radHit, reason: "radical", detail: `「${rad}」` });
    } else {
      const strokes = charStrokes?.[i] ?? 0;
      const el = elementByStroke(strokes);
      counts[el]++;
      perChar.push({
        char: c,
        element: el,
        reason: "stroke",
        detail: `笔画 ${strokes}（末位 ${strokes % 10}）`,
      });
    }
  }

  // 主导 = 计数最高的
  let dominant: Element = "土";
  let max = -1;
  for (const el of ["金", "木", "水", "火", "土"] as Element[]) {
    if (counts[el] > max) {
      max = counts[el];
      dominant = el;
    }
  }

  // 主导原因
  const dominantReasons: string[] = [];
  const dominantChars = perChar.filter((p) => p.element === dominant);
  for (const m of dominantChars) {
    if (m.reason === "radical") {
      dominantReasons.push(`「${m.char}」含部首 ${m.detail} → ${m.element}`);
    } else {
      dominantReasons.push(`「${m.char}」无部首，按${m.detail} → ${m.element}`);
    }
  }
  if (dominantChars.length > 0) {
    dominantReasons.push(
      `共 ${chars.length} 字中 ${dominantChars.length} 字归${dominant}，主导 = ${dominant}`
    );
  }

  // 缺失原因
  const missingReasons: Record<Element, string> = {
    金: "",
    木: "",
    水: "",
    火: "",
    土: "",
  };
  for (const el of ELEMENTS) {
    if (counts[el] === 0) {
      const samples = ELEMENT_SAMPLE_RADICALS[el].join("、");
      missingReasons[el] = `名字中无「${samples}」等${el}部字`;
    }
  }

  // 总结
  const missingList = ELEMENTS.filter((el) => counts[el] === 0);
  const summary =
    missingList.length === 0
      ? `你五行俱全，「${raw}」每行都有字根`
      : `「${raw}」五行不全，缺 ${missingList.join("·")}`;

  return {
    perChar,
    dominantReasons,
    missingReasons,
    summary,
  };
}